import React, { useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { exportToCSV, exportToExcel } from "../../lib/exporter";
import { Users, CreditCard, CalendarCheck, Award, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const baseExports = [
  { id: "students", label: "All Students", icon: Users, color: "blue", coll: "students" },
];

const controlExports = [
  ...baseExports,
  { id: "payments", label: "Payments", icon: CreditCard, color: "emerald", coll: "payments" },
  { id: "attendance", label: "Attendance Records", icon: CalendarCheck, color: "amber", coll: "attendance" },
  { id: "certificates", label: "Certificates", icon: Award, color: "violet", coll: "certificates" },
  { id: "enrollments", label: "Enrollments", icon: FileDown, color: "sky", coll: "enrollments" },
];

const AdminExport = () => {
  const { profile } = useAuth();
  const [busy, setBusy] = useState(null);
  const availableExports = profile?.hasControls ? controlExports : baseExports;

  const doExport = async (id, coll, format) => {
    setBusy(`${id}-${format}`);
    try {
      const snap = await getDocs(collection(db, coll));
      const data = snap.docs.map((d) => {
        const { createdAt, updatedAt, markedAt, enrolledAt, ...rest } = d.data();
        return { ...rest };
      });
      if (data.length === 0) { toast.error("No data to export"); return; }
      if (format === "csv") exportToCSV(data, `${id}.csv`);
      else exportToExcel(data, `${id}.xlsx`, id);
      toast.success(`Exported ${data.length} records`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    sky: "bg-sky-50 text-sky-700",
  };

  return (
    <div data-testid="admin-export">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Export Data</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">CSV / Excel Export</h1>
        <p className="mt-1 text-slate-600">Download platform data in CSV or Excel format.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableExports.map((e) => (
          <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-6">
            <div className={`w-12 h-12 rounded-lg ${colorMap[e.color]} flex items-center justify-center mb-4`}>
              <e.icon className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-semibold text-slate-900 mb-1">{e.label}</h3>
            <p className="text-xs text-slate-500 mb-4">Export all {e.label.toLowerCase()} from Firestore.</p>
            <div className="flex gap-2">
              <button onClick={() => doExport(e.id, e.coll, "csv")} disabled={busy === `${e.id}-csv`} data-testid={`export-${e.id}-csv-btn`} className="flex-1 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-semibold disabled:opacity-50">
                {busy === `${e.id}-csv` ? "..." : "CSV"}
              </button>
              <button onClick={() => doExport(e.id, e.coll, "xlsx")} disabled={busy === `${e.id}-xlsx`} data-testid={`export-${e.id}-xlsx-btn`} className="flex-1 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50">
                {busy === `${e.id}-xlsx` ? "..." : "Excel"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminExport;
